import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Message } from 'primeng/api';
import { Subject, zip } from 'rxjs';
import { LangService } from 'src/app/common/service/lang.service';

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  private readonly TOAST_DURATION: number = 4000; // ms

  private toastSubscription = new Subject<Message>();
  toast$ = this.toastSubscription.asObservable();

  constructor(private translate: TranslateService, private langService: LangService) { }

  private showToast(message: Message): void {
    this.toastSubscription.next(message);
  }

  private showSimpleToast(
    titleKey: string, titleParams: any, messageKey: string, messageParams: any, severity: string = 'info',
    nestedTranslationEnabled: boolean, life: number = this.TOAST_DURATION
  ): void {
    zip(
      nestedTranslationEnabled
        ? this.langService.translateNested(titleKey || 'information', titleParams || {})
        : this.translate.get(titleKey || 'information', titleParams || {}),
      nestedTranslationEnabled
        ? this.langService.translateNested(messageKey || 'information', messageParams || {})
        : this.translate.get(messageKey || 'information', messageParams || {})
    ).subscribe(
      ([title, message]: [string, string]) => {
        this.showToast({
          severity,
          summary: title,
          detail: message,
          life
        });
      }
    );
  }

  reportGenerationToast(isError: boolean, messageAndTitleParams: any = { reportOrChart2: 'report2' }): void {
    const severity = isError ? 'error' : 'info';
    const messageKey = isError ? 'reportGenerationError' : 'reportGenerationAddedToQueue';
    this.showSimpleToast('reportGeneration', messageAndTitleParams, messageKey, messageAndTitleParams, severity, true);
  }

  showInfoToast(
    messageKey: string, titleKey: string = null, messageParams: any = null, titleParams: any = null, nestedTranslationEnabled: boolean = false
  ): void {
    this.showSimpleToast(titleKey, titleParams, messageKey, messageParams, 'info', nestedTranslationEnabled);
  }

  showErrorToast(
    messageKey: string, titleKey: string = 'error', messageParams: any = null, titleParams: any = null, nestedTranslationEnabled: boolean = false
  ): void {
    this.showSimpleToast(titleKey, titleParams, messageKey, messageParams, 'error', nestedTranslationEnabled);
  }

  showWarnToast(
    messageKey: string, titleKey: string = null, messageParams: any = null, titleParams: any = null, nestedTranslationEnabled: boolean = false
  ): void {
    this.showSimpleToast(titleKey, titleParams, messageKey, messageParams, 'warn', nestedTranslationEnabled);
  }

  showDataToast(title: string, message: string, data: any, severity: string = 'info', life: number = this.TOAST_DURATION): void {
    this.showToast({
      severity,
      summary: title,
      detail: message,
      life,
      data
    });
  }

  showStickyToast(titleKey: string, titleParams: any, messageKey: string, messageParams: any, severity: string = 'error'): void {
    this.showStickyDataToast(titleKey, titleParams, messageKey, messageParams, severity);
  }

  showTimedToast(titleKey: string, titleParams: any, messageKey: string, messageParams: any, severity: string = 'info', life: number = 10000): void {
    zip(
      this.translate.get(titleKey, titleParams),
      this.translate.get(messageKey, messageParams)
    ).subscribe(([title, message]: [string, string]) => {
      this.showToast({ severity, summary: title, detail: message, life });
    });
  }

  showStickyDataToast(titleKey: string, titleParams: any, messageKey: string, messageParams: any, data: any = null, severity: string = 'error'): void {
    zip(
      this.translate.get(titleKey, titleParams),
      this.translate.get(messageKey, messageParams)
    ).subscribe(
      ([title, message]: [string, string]) => {
        this.showToast({
          severity,
          summary: title,
          detail: message,
          sticky: true,
          data
        });
      }
    );
  }
}
